import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import InstallGate from "../components/InstallGate";
import { Crown, Check, X, MapPin, Hash } from "lucide-react";
import { toast } from "sonner";

export default function AcceptInvite() {
  return (
    <InstallGate
      message="Install Proximity to accept"
      sublabel="You received an invite. Add Proximity to your home screen to accept it — then reopen the invite link from Proximity."
    >
      <AcceptInviteContent />
    </InstallGate>
  );
}

function AcceptInviteContent() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading, refresh } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deviceName, setDeviceName] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [manualCode, setManualCode] = useState("");

  useEffect(() => {
    if (!code) { setLoading(false); return; }
    (async () => {
      try {
        const r = await api.get(`/devices/invite/${code}`);
        setPreview(r.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Invitation not found");
      } finally { setLoading(false); }
    })();
  }, [code]);

  const accept = async () => {
    // Phase 3: require authentication before accepting
    if (!user) {
      localStorage.setItem("pending_invite", JSON.stringify({
        kind: "device",
        token: code,
        device_name: deviceName.trim() || undefined,
      }));
      navigate(`/signup?next=${encodeURIComponent(`/accept/${code}`)}`);
      return;
    }
    setAccepting(true);
    try {
      const r = await api.post(`/devices/invite/${code}/accept`, {
        device_name: deviceName.trim() || undefined,
      });
      // Store session token for cookieless devices
      if (r.data?.session_token) {
        localStorage.setItem("session_token", r.data.session_token);
      }
      localStorage.removeItem("pending_invite");
      toast.success(`Connected to ${r.data.master_name}'s plan`);
      // Hard-redirect so the AuthProvider rehydrates with the new session
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't accept");
      setAccepting(false);
    }
  };

  // After auth, auto-resume pending device invite
  useEffect(() => {
    if (!user || !preview || accepting) return;
    const pending = (() => {
      try { return JSON.parse(localStorage.getItem("pending_invite") || "null"); } catch { return null; }
    })();
    if (pending?.kind === "device" && pending?.token === code) {
      if (pending.device_name && !deviceName) setDeviceName(pending.device_name);
      localStorage.removeItem("pending_invite");
      accept();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, preview, code]);

  const goManual = () => {
    const c = manualCode.trim();
    if (c.length < 4) { toast.error("Enter the 6-digit code"); return; }
    navigate(`/accept/${c}`);
  };

  // No code in URL — show code entry
  if (!code) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-50 grid place-items-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="size-16 rounded-2xl bg-[#007AFF] grid place-items-center mx-auto">
            <Hash size={28} className="text-white"/>
          </div>
          <h1 className="font-display font-black text-3xl tracking-tighter mt-5">Enter access code</h1>
          <p className="text-sm text-zinc-400 mt-2">Got a 6-digit code from someone? Enter it below to join their plan.</p>
          <div className="mt-6 space-y-3">
            <input
              data-testid="manual-code-input"
              inputMode="numeric"
              pattern="[0-9]*"
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value.replace(/\D/g,"").slice(0,6))}
              placeholder="000000"
              className="w-full text-center font-mono font-black text-3xl tracking-[0.3em] py-4 rounded-xl border border-white/10 bg-white/[0.03] focus:outline-none focus:border-[#007AFF]"
            />
            <button
              data-testid="manual-continue-btn"
              onClick={goManual}
              className="w-full bg-[#007AFF] hover:bg-[#005ec4] text-white font-bold py-3 rounded-full"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-50 grid place-items-center p-6">
        <div className="text-center" data-testid="accept-loading-state">
          <div className="label-eyebrow">Loading invitation…</div>
          {/* Always give the user a way out — this screen must never trap
              them if the preview lookup hangs (network / server slow). */}
          <button
            data-testid="accept-loading-cancel-btn"
            onClick={() => navigate("/", { replace: true })}
            className="mt-6 text-sm text-zinc-500 underline"
          >
            Cancel and go home
          </button>
        </div>
      </div>
    );
  }

  if (error || !preview) {
    return (
      <div className="min-h-screen bg-zinc-950 text-zinc-50 grid place-items-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="size-16 rounded-2xl bg-[#FF3B30]/15 grid place-items-center mx-auto">
            <X size={28} className="text-[#FF3B30]"/>
          </div>
          <h1 className="font-display font-black text-3xl tracking-tighter mt-5">Invitation unavailable</h1>
          <p className="text-sm text-zinc-400 mt-2">{error || "This invitation is no longer valid."}</p>
          <button
            data-testid="decline-go-home"
            onClick={() => navigate("/")}
            className="mt-6 w-full bg-white/5 border border-white/10 font-bold py-3 rounded-full"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 grid place-items-center p-6">
      <div className="w-full max-w-md">
        {/* Identity */}
        <div className="text-center">
          <div className="size-20 rounded-full bg-zinc-800 mx-auto overflow-hidden grid place-items-center font-display font-black text-3xl">
            {preview.master_picture
              ? <img src={preview.master_picture} alt="" className="w-full h-full object-cover"/>
              : (preview.master_name || "?").slice(0,1)}
          </div>
          <div className="label-eyebrow mt-4 flex items-center justify-center gap-1.5"><Crown size={12} className="text-[#FFCC00]"/> Proximity invitation</div>
          <h1 className="font-display font-black text-3xl tracking-tighter mt-2" data-testid="invite-headline">
            Accept invitation from {preview.master_name}?
          </h1>
          <p className="text-sm text-zinc-400 mt-2">
            You&apos;ll be added to their plan as a connected device. They&apos;ll see your location on the map and vice versa.
          </p>
        </div>

        {/* Optional name */}
        <div className="mt-6">
          <div className="label-eyebrow mb-2 flex items-center gap-1.5"><MapPin size={12}/> Name this device (optional)</div>
          <input
            data-testid="device-name-input"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value.slice(0, 40))}
            placeholder="e.g. Tommy's iPhone"
            className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#007AFF]"
          />
        </div>

        {/* Actions */}
        <div className="mt-6 flex gap-2">
          <button
            data-testid="decline-btn"
            onClick={() => navigate("/")}
            className="flex-1 py-3 rounded-full border border-white/10 font-bold inline-flex items-center justify-center gap-2"
          >
            <X size={15}/> Decline
          </button>
          <button
            data-testid="accept-btn"
            onClick={accept}
            disabled={accepting}
            className="flex-1 py-3 rounded-full bg-[#34C759] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            <Check size={16}/> {accepting ? "Joining…" : user ? "Accept" : "Sign up & accept"}
          </button>
        </div>

        <div className="mt-5 text-[11px] text-zinc-600 text-center">
          By accepting, your real-time location will be shared with {preview.master_name}. You can leave anytime — and they can remove your device too.
        </div>
      </div>
    </div>
  );
}
